import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import { AnnouncementNotice } from "../components/AnnouncementBanner";
import { RichSummary } from "../components/RichSummary";
import {
  LIFECYCLE_STATES,
  REMINDER_LEADS,
  SAIcon,
  SERVICE_PILL,
  SERVICE_STATUS,
  SERVICE_STATUS_LABEL,
  TYPE_META,
  emailTone,
  fmtAwstShort,
  stateLabel,
  windowDuration,
  windowParts,
  type SAIconName,
} from "../components/statusAlerts";
import {
  SUMMARY_LIMIT,
  applySummaryEdit,
  continueList,
  insertLink,
  pasteLink,
  toggleBold,
  toggleBulletList,
  toggleNumberedList,
  type SummaryEdit,
} from "../components/summaryFormatting";
import {
  api,
  type AlertType,
  type AnnouncementAdmin,
  type RecipientFunnel,
  type RenderedSummary,
  type ServiceItem,
  type StatusAlertClient,
  type AnnouncementBodyFormat,
  type Me,
} from "../services/api";

// Per-type defaults applied when a fresh alert is created or the type is
// switched (Implementation Spec §5: switching type resets copy, services,
// lifecycle + timing defaults). No em-dashes in the default copy (Blake's rule).
const TYPE_DEFAULTS: Record<
  AlertType,
  { title: string; site: string; lede: string; services: ServiceItem[]; reminder: boolean }
> = {
  outage: {
    title: "Multiple service outage",
    site: "Welshpool data centre",
    lede: "Our IT team is actively investigating and will post updates as the situation develops.",
    services: [
      { name: "Autotask · Ticketing", description: "Logging & updating tickets", status: "down" },
      { name: "Microsoft 365 · Email", description: "Inbound & outbound mailflow", status: "degraded" },
      { name: "VPN · Remote access", description: "Off-site connections", status: "down" },
    ],
    reminder: false,
  },
  maintenance: {
    title: "Welshpool server maintenance",
    site: "Welshpool data centre",
    lede: "This is planned, routine work. No fault is involved. Expect brief downtime during the window.",
    services: [
      { name: "File Server · Welshpool", description: "Shared drives & documents", status: "scheduled" },
      { name: "Autotask · Ticketing", description: "Logging & updating tickets", status: "scheduled" },
      { name: "Microsoft 365 · Email", description: "Mailflow unaffected", status: "noimpact" },
    ],
    reminder: true,
  },
  enhancement: {
    title: "New shared document workspace",
    site: "Microsoft 365",
    lede: "New functionality that makes collaboration faster and easier for your team is rolling out soon.",
    services: [
      { name: "Shared document workspace", description: "One place for team files", status: "new" },
      { name: "Real-time co-authoring", description: "Edit together", status: "new" },
      { name: "Version history & restore", description: "Roll back any version", status: "improved" },
    ],
    reminder: true,
  },
};

const SEVERITY_OF: Record<AlertType, "incident" | "maintenance" | "info"> = {
  outage: "incident",
  maintenance: "maintenance",
  enhancement: "info",
};

const PUBLISH_LABEL: Record<AlertType, string> = {
  outage: "Publish & send alert",
  maintenance: "Schedule & notify",
  enhancement: "Announce & notify",
};

interface DraftState {
  alertType: AlertType;
  state: string;
  reference: string;
  affectedSite: string;
  title: string;
  lede: string;
  services: ServiceItem[];
  visibility: "specific" | "all";
  selected: string[]; // company ids
  windowStart: string; // datetime-local
  windowEnd: string;
  sendMode: "now" | "schedule";
  sendAt: string;
  reminderOn: boolean;
  reminderLead: string;
  emailClients: boolean; // false = "banner only", no customer email (#39)
  // Stored format of a loaded row. New alerts are saved as markdown. An
  // existing plain alert stays plain until the summary text differs from
  // the loaded text.
  bodyFormat: AnnouncementBodyFormat;
}

function defaultsForType(type: AlertType): Pick<
  DraftState,
  "state" | "title" | "affectedSite" | "lede" | "services" | "reminderOn" | "reminderLead"
> {
  const d = TYPE_DEFAULTS[type];
  return {
    state: LIFECYCLE_STATES[type][0][0],
    title: d.title,
    affectedSite: d.site,
    lede: d.lede,
    services: d.services.map((s) => ({ ...s })),
    reminderOn: d.reminder,
    reminderLead: REMINDER_LEADS[type][0]?.[0] ?? "",
  };
}

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function localToIso(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

function makeInitial(a: AnnouncementAdmin | null): DraftState {
  if (!a) {
    const type: AlertType = "outage";
    return {
      alertType: type,
      reference: "",
      visibility: "specific",
      selected: [],
      windowStart: "",
      windowEnd: "",
      sendMode: "now",
      sendAt: "",
      emailClients: true,
      bodyFormat: "plain",
      ...defaultsForType(type),
    };
  }
  const type: AlertType =
    a.alert_type ??
    (a.severity === "incident" ? "outage" : a.severity === "maintenance" ? "maintenance" : "enhancement");
  const d = defaultsForType(type);
  return {
    alertType: type,
    state: a.lifecycle_state ?? d.state,
    reference: a.reference ?? "",
    affectedSite: a.affected_site ?? d.affectedSite,
    title: a.title,
    lede: a.body,
    services: a.services && a.services.length ? a.services.map((s) => ({ ...s })) : [],
    visibility: a.target_all ? "all" : "specific",
    selected: a.target_company_ids ?? [],
    windowStart: toDatetimeLocal(a.window_start),
    windowEnd: toDatetimeLocal(a.window_end),
    sendMode: a.send_at ? "schedule" : "now",
    sendAt: toDatetimeLocal(a.send_at),
    reminderOn: a.reminder_lead != null,
    reminderLead: a.reminder_lead ?? d.reminderLead,
    emailClients: a.email_enabled ?? true,
    bodyFormat: a.body_format === "markdown" ? "markdown" : "plain",
  };
}

/** Mirror of services/announcements._compute_reminder_at, for the live recap. */
function reminderAtLocal(windowStart: string, lead: string): Date | null {
  if (!windowStart || !lead) return null;
  const base = new Date(windowStart);
  if (Number.isNaN(base.getTime())) return null;
  const d = new Date(base);
  if (lead === "24h") d.setHours(d.getHours() - 24);
  else if (lead === "2h") d.setHours(d.getHours() - 2);
  else if (lead === "week") {
    d.setDate(d.getDate() - 7);
    d.setHours(9, 0, 0, 0);
  } else if (lead === "day_before") {
    d.setDate(d.getDate() - 1);
    d.setHours(9, 0, 0, 0);
  } else if (lead === "morning") {
    d.setHours(8, 0, 0, 0);
  }
  return d;
}

export function AdminStatusAlertComposer() {
  const { id } = useParams();
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const { data: clients } = useSWR("sa-clients", () =>
    api.announcements.listClients(),
  );
  const { data: existing, isLoading: loadingExisting } = useSWR(
    id ? `/api/admin/announcements/${id}` : null,
    () => api.announcements.adminGet(id as string),
  );

  if (me && me.role !== "iquest_admin" && me.role !== "service_desk") {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }
  if (!me || !clients || (id && (loadingExisting || !existing))) {
    return <div className="card">Loading composer…</div>;
  }

  return (
    <ComposerForm
      key={id ?? "new"}
      existingId={id ?? null}
      initial={makeInitial(existing ?? null)}
      clients={clients}
      testSendEnabled={me.status_alert_test_send_enabled === true}
    />
  );
}

function ComposerForm({
  existingId,
  initial,
  clients,
  testSendEnabled,
}: {
  existingId: string | null;
  initial: DraftState;
  clients: StatusAlertClient[];
  testSendEnabled: boolean;
}) {
  const navigate = useNavigate();
  const clientById = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients],
  );

  const [d, setD] = useState<DraftState>(initial);
  const [id, setId] = useState<string | null>(existingId);
  const [preview, setPreview] = useState<"email" | "banner">("email");
  const [previewAs, setPreviewAs] = useState<string | null>(null);
  const [funnel, setFunnel] = useState<RecipientFunnel | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // A loaded plain alert stays plain until the Summary field differs from
  // the loaded text. Reverting to that exact text keeps plain. New alerts
  // and rows already saved as markdown stay markdown.
  const loadedPlain = existingId !== null && initial.bodyFormat !== "markdown";
  const summaryChanged = d.lede !== initial.lede;
  const summaryTooLong = d.lede.length > SUMMARY_LIMIT;
  const showConversion = loadedPlain && !summaryChanged;
  const previewFormat: AnnouncementBodyFormat =
    loadedPlain && !summaryChanged ? "plain" : "markdown";
  const summaryPreview = useLiveSummaryPreview(
    d.lede,
    d.alertType,
    summaryHasWindow(d.alertType, d.windowStart, d.windowEnd),
    previewFormat,
  );

  const meta = TYPE_META[d.alertType];
  // Banner-only alerts (email off) publish immediately: there is no email to
  // schedule or remind about, so scheduling is disabled (#39).
  const scheduleMode =
    d.emailClients && d.alertType !== "outage" && d.sendMode === "schedule";
  const publishLabel = d.emailClients
    ? PUBLISH_LABEL[d.alertType]
    : "Publish banner only";
  const publishIcon: SAIconName = !d.emailClients
    ? "eye"
    : scheduleMode
      ? "clock"
      : "mail";
  // Banner-only alerts have no email to preview, so the pane stays on Banner.
  const effectivePreview = d.emailClients ? preview : "banner";

  // ---- patch helpers ----
  const patch = useCallback(
    (p: Partial<DraftState>) => setD((prev) => ({ ...prev, ...p })),
    [],
  );

  function setType(type: AlertType) {
    // Switching type resets copy, services, lifecycle and timing to the type's
    // template (Implementation Spec §5).
    setD((prev) => ({
      ...prev,
      alertType: type,
      reference: "",
      windowStart: "",
      windowEnd: "",
      sendMode: "now",
      sendAt: "",
      ...defaultsForType(type),
    }));
  }

  // ---- recipient funnel (debounced live resolve) ----
  const targetCompanyIds = d.visibility === "all" ? null : d.selected;
  const resolveKey = `${d.alertType}|${d.visibility}|${(targetCompanyIds ?? []).join(",")}`;
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const f = await api.announcements.resolveRecipients({
          alert_type: d.alertType,
          target_all: d.visibility === "all",
          target_company_ids: d.visibility === "all" ? null : d.selected,
        });
        if (!cancelled) setFunnel(f);
      } catch {
        if (!cancelled) setFunnel(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveKey]);

  // ---- previewing-as: keep pointed at a targeted client ----
  const targetedClients = useMemo(() => {
    if (d.visibility === "all") return clients;
    return d.selected.map((cid) => clientById[cid]).filter(Boolean);
  }, [d.visibility, d.selected, clients, clientById]);

  useEffect(() => {
    if (d.visibility === "all") {
      setPreviewAs(null);
      return;
    }
    if (targetedClients.length === 0) {
      setPreviewAs(null);
    } else if (!previewAs || !d.selected.includes(previewAs)) {
      setPreviewAs(d.selected[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.visibility, d.selected]);

  const multiClient = d.visibility === "all" || targetedClients.length > 1;
  const previewClientName =
    d.visibility === "all"
      ? null
      : (previewAs && clientById[previewAs]?.name) ?? null;

  // ---- toast ----
  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  }

  // ---- persistence ----
  function formatFor(kind: "save" | "publish" | "test"): "markdown" | "plain" {
    if (kind === "test" || !loadedPlain || summaryChanged) return "markdown";
    return "plain";
  }

  function buildPayload(bodyFormat: "markdown" | "plain") {
    return {
      title: d.title.trim(),
      body: d.lede.trim(),
      body_format: bodyFormat,
      alert_type: d.alertType,
      reference: d.reference.trim() || null,
      lifecycle_state: d.state,
      affected_site: d.affectedSite.trim() || null,
      services: d.services,
      target_all: d.visibility === "all",
      target_company_ids: d.visibility === "all" ? null : d.selected,
      window_start: localToIso(d.windowStart),
      window_end: d.alertType === "maintenance" ? localToIso(d.windowEnd) : null,
      send_at: scheduleMode ? localToIso(d.sendAt || d.windowStart) : null,
      reminder_lead:
        d.emailClients && d.alertType !== "outage" && d.reminderOn
          ? d.reminderLead
          : null,
      email_enabled: d.emailClients,
    };
  }

  function validate(): string | null {
    if (!d.title.trim()) return "Add a headline before saving.";
    if (!d.lede.trim()) return "Add a summary before saving.";
    if (d.lede.length > SUMMARY_LIMIT) return "Keep the summary under 4,000 characters.";
    if (d.visibility === "specific" && d.selected.length === 0)
      return "Choose at least one client, or switch to All clients.";
    return null;
  }

  async function persist(kind: "save" | "publish"): Promise<AnnouncementAdmin> {
    const bodyFormat = formatFor(kind);
    const payload = buildPayload(bodyFormat);
    const saved = id
      ? await api.announcements.adminUpdate(id, payload)
      : await api.announcements.adminCreate({
          ...payload,
          is_draft: true,
        });
    if (!id) setId(saved.id);
    return saved;
  }

  function buildTestSendPayload() {
    const payload = buildPayload("markdown");
    return {
      title: payload.title,
      body: payload.body,
      body_format: "markdown" as const,
      alert_type: d.alertType,
      lifecycle_state: d.state,
      reference: payload.reference,
      affected_site: payload.affected_site,
      services: d.services,
      window_start: payload.window_start,
      window_end: payload.window_end,
      target_all: payload.target_all,
      target_company_ids: payload.target_company_ids,
    };
  }

  function toastError(e: unknown, fallback: string): string {
    if (!(e instanceof Error) || !e.message) return fallback;
    const start = e.message.indexOf("{");
    if (start >= 0) {
      try {
        const body = JSON.parse(e.message.slice(start)) as { detail?: unknown };
        if (typeof body.detail === "string" && body.detail.trim()) return body.detail;
      } catch {
        // Keep the raw message when the body is not JSON.
      }
    }
    return e.message;
  }

  async function onSendTest() {
    const err = validate();
    if (err) {
      showToast(err);
      return;
    }
    setBusy(true);
    try {
      const result = await api.announcements.testSend(buildTestSendPayload());
      const clientName = result.rendered_for_company_name
        ? ` Greeting uses ${result.rendered_for_company_name}.`
        : "";
      showToast(`Test email sent to ${result.sent_to}.${clientName}`);
    } catch (e) {
      showToast(toastError(e, "Could not send the test email"));
    } finally {
      setBusy(false);
    }
  }

  async function onSaveDraft() {
    const err = validate();
    if (err) {
      showToast(err);
      return;
    }
    setBusy(true);
    try {
      await persist("save");
      showToast("Draft saved");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not save draft");
    } finally {
      setBusy(false);
    }
  }

  async function onPublish() {
    const err = validate();
    if (err) {
      showToast(err);
      return;
    }
    setBusy(true);
    try {
      const saved = await persist("publish");
      const recip = funnel?.will_email ?? 0;
      if (scheduleMode) {
        showToast(
          `Scheduled: up to ${recip.toLocaleString()} recipients will be emailed at ${fmtAwstShort(
            localToIso(d.sendAt || d.windowStart),
          )}.`,
        );
      } else {
        await api.announcements.postUpdate(saved.id, { state: d.state });
        showToast(
          d.emailClients
            ? `Published: ${stateLabel(d.state)} posted and emailing up to ${recip.toLocaleString()} recipients.`
            : `Published: ${stateLabel(d.state)} posted to the portal banner. No email sent.`,
        );
      }
      window.setTimeout(() => navigate("/admin/announcements"), 900);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not publish");
      setBusy(false);
    }
  }

  function onRefresh() {
    setRefreshing(true);
    window.setTimeout(() => setRefreshing(false), 600);
    showToast("Customer display regenerated");
  }

  // ---- draggable splitter (persisted) ----
  const splitRef = useRef<HTMLDivElement>(null);
  const [paneW, setPaneW] = useState<number>(() => {
    const saved = parseFloat(localStorage.getItem("sa-pane-w") || "");
    return Number.isFinite(saved) && saved >= 360 ? saved : 480;
  });
  const dragging = useRef(false);

  const clampPane = useCallback((w: number) => {
    const el = splitRef.current;
    const total = el ? el.getBoundingClientRect().width : 1000;
    const max = Math.max(360, total - 7 - 380);
    return Math.min(max, Math.max(360, w));
  }, []);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      if (!dragging.current || !splitRef.current) return;
      const right = splitRef.current.getBoundingClientRect().right;
      setPaneW(clampPane(right - e.clientX));
    }
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.classList.remove("sa-resizing");
      setPaneW((w) => {
        localStorage.setItem("sa-pane-w", String(w));
        return w;
      });
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    }
    function onDown() {
      dragging.current = true;
      document.body.classList.add("sa-resizing");
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }
    const grip = document.getElementById("sa-resizer");
    grip?.addEventListener("pointerdown", onDown);
    return () => {
      grip?.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [clampPane]);

  // ---- services editing ----
  function updateService(i: number, p: Partial<ServiceItem>) {
    setD((prev) => {
      const services = prev.services.slice();
      services[i] = { ...services[i], ...p };
      return { ...prev, services };
    });
  }
  function addService() {
    setD((prev) => ({
      ...prev,
      services: [
        ...prev.services,
        { name: "New service", description: "Describe the impact", status: SERVICE_STATUS[prev.alertType][0][0] },
      ],
    }));
  }
  function removeService(i: number) {
    setD((prev) => ({ ...prev, services: prev.services.filter((_, j) => j !== i) }));
  }

  // ---- toggle a client in the picker ----
  function toggleClient(cid: string) {
    setD((prev) => ({
      ...prev,
      selected: prev.selected.includes(cid)
        ? prev.selected.filter((x) => x !== cid)
        : [...prev.selected, cid],
    }));
  }

  const splitStyle: CSSProperties = {
    // Pane width lives in a variable so the stylesheet media query can stack
    // the split. An inline grid-template-columns beats that query, which left
    // the two columns in place through the 1100-1166px band (issue #266).
    ["--sa-pane-w" as string]: `${paneW}px`,
  };

  return (
    <div className="sa-comp">
      {/* top bar */}
      <div className="sa-comp-bar">
        <button
          type="button"
          className="sa-comp-back"
          onClick={() => navigate("/admin/announcements")}
        >
          <SAIcon name="arrowR" /> Status Alerts
        </button>
        <div className="sa-comp-titlewrap">
          <span
            className={`sa-glyph ${d.alertType}`}
            style={{ width: 30, height: 30, borderRadius: 8 }}
          >
            <SAIcon name={meta.glyph} />
          </span>
          <span className="sa-comp-title">{d.title || "New status alert"}</span>
          <span className={`sa-chip ${d.state}`}>
            <span className="d" />
            {stateLabel(d.state)}
          </span>
        </div>
        <div className="sa-comp-actions">
          <span className="sa-autosave">
            <span className="d" /> {id ? "Saved" : "Draft"}
          </span>
          {testSendEnabled && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onSendTest}
              disabled={busy || summaryTooLong}
              title={
                summaryTooLong
                  ? "Keep the summary under 4,000 characters."
                  : "Emails this draft to your own address. Clients are not contacted."
              }
            >
              Send test to me
            </button>
          )}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onSaveDraft}
              disabled={busy || summaryTooLong}
              title={summaryTooLong ? "Keep the summary under 4,000 characters." : undefined}
            >
              Save draft
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onPublish}
              disabled={busy || summaryTooLong}
              aria-label={publishLabel}
              title={summaryTooLong ? "Keep the summary under 4,000 characters." : undefined}
            >
            <span style={{ display: "inline-flex", width: 15, height: 15 }} aria-hidden="true">
              <SAIcon name={publishIcon} />
            </span>{" "}
            {publishLabel}
          </button>
        </div>
      </div>

      <div className="sa-split" ref={splitRef} style={splitStyle}>
        {/* ===== form ===== */}
        <div className="sa-form">
          <div className="sa-formwrap">
            {/* 1 — type */}
            <Section n={1} title="Alert type" hint="sets tone, icon & reference">
              <div className="sa-typeseg">
                {(Object.keys(TYPE_META) as AlertType[]).map((t) => {
                  const tm = TYPE_META[t];
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`sa-typeopt ${t === d.alertType ? "active" : ""}`}
                      style={{ ["--accent" as string]: tm.tone }}
                      onClick={() => setType(t)}
                    >
                      <span
                        className={`sa-glyph ${t}`}
                        style={{ width: 34, height: 34, borderRadius: 9 }}
                      >
                        <SAIcon name={tm.glyph} />
                      </span>
                      <div className="nm">{tm.label}</div>
                      <div className="ds">{tm.desc}</div>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* 2 — current status */}
            <Section
              n={2}
              title="Current status"
              hint={LIFECYCLE_STATES[d.alertType].map((s) => s[1]).join(" → ")}
            >
              <div className="sa-states">
                {LIFECYCLE_STATES[d.alertType].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`sa-stateopt ${key === d.state ? "active" : ""}`}
                    style={{ ["--accent" as string]: meta.tone }}
                    onClick={() => patch({ state: key })}
                  >
                    <span className="d" />
                    {label}
                  </button>
                ))}
              </div>
            </Section>

            {/* 3 — details */}
            <Section n={3} title="Details">
              <div className="sa-row2">
                <Field label="Reference (Autotask ticket / change no.)">
                  <input
                    className="sa-in"
                    value={d.reference}
                    placeholder={`${meta.refPrefix}-2026-0000`}
                    onChange={(e) => patch({ reference: e.target.value })}
                  />
                </Field>
                <Field label="Affected site / system">
                  <input
                    className="sa-in"
                    value={d.affectedSite}
                    onChange={(e) => patch({ affectedSite: e.target.value })}
                  />
                </Field>
              </div>
              <Field label="Headline">
                <input
                  className="sa-in"
                  value={d.title}
                  maxLength={200}
                  onChange={(e) => patch({ title: e.target.value })}
                />
              </Field>
              <SummaryField
                value={d.lede}
                onChange={(lede) => patch({ lede })}
                warnings={summaryWarnings(summaryPreview, summaryTooLong)}
                overLimit={summaryTooLong}
                showConversion={showConversion}
              />
            </Section>

            {/* 4 — services */}
            <Section
              n={4}
              title="Affected services"
              hint={`${d.services.length} service${d.services.length === 1 ? "" : "s"}`}
            >
              <div>
                {d.services.map((s, i) => (
                  <div className="sa-svc-row" key={i}>
                    <div className="nm">
                      <input
                        className="sa-svc-name"
                        value={s.name}
                        placeholder="Service name"
                        aria-label="Service name"
                        onChange={(e) => updateService(i, { name: e.target.value })}
                      />
                      <input
                        className="sa-svc-sub"
                        value={s.description}
                        placeholder="Describe the impact"
                        aria-label="Impact description"
                        onChange={(e) => updateService(i, { description: e.target.value })}
                      />
                    </div>
                    <select
                      className="sa-svc-status"
                      value={s.status}
                      onChange={(e) => updateService(i, { status: e.target.value })}
                    >
                      {SERVICE_STATUS[d.alertType].map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="sa-svc-del"
                      title="Remove service"
                      aria-label="Remove service"
                      onClick={() => removeService(i)}
                    >
                      <SAIcon name="trash" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="sa-add-svc" onClick={addService}>
                <SAIcon name="plus" /> Add a service
              </button>
            </Section>

            {/* 5 — visibility */}
            <Section n={5} title="Portal visibility" hint="who sees the banner">
              <div className="sa-vis-grid">
                <button
                  type="button"
                  className={`sa-vis-opt ${d.visibility === "specific" ? "active" : ""}`}
                  onClick={() => patch({ visibility: "specific" })}
                >
                  <span className="ic">
                    <SAIcon name="building" />
                  </span>
                  <div>
                    <div className="nm">Specific clients</div>
                    <div className="ds">
                      Only the tenants you choose see the banner &amp; get emailed.
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  className={`sa-vis-opt ${d.visibility === "all" ? "active" : ""}`}
                  onClick={() => patch({ visibility: "all" })}
                >
                  <span className="ic">
                    <SAIcon name="globe" />
                  </span>
                  <div>
                    <div className="nm">All clients</div>
                    <div className="ds">
                      Every managed tenant. Use for platform-wide events.
                    </div>
                  </div>
                </button>
              </div>
              {d.visibility === "specific" && (
                <div>
                  <label className="sa-fl">Choose clients</label>
                  <div className="sa-client-list">
                    {clients.map((c) => {
                      const on = d.selected.includes(c.id);
                      return (
                        <div
                          key={c.id}
                          className={`sa-client ${on ? "on" : ""}`}
                          onClick={() => toggleClient(c.id)}
                          role="checkbox"
                          aria-checked={on}
                          tabIndex={0}
                          onKeyDown={(e) =>
                            (e.key === "Enter" || e.key === " ") &&
                            (e.preventDefault(), toggleClient(c.id))
                          }
                        >
                          <span className="cb">
                            <SAIcon name="check" />
                          </span>
                          <span className="nm">{c.name}</span>
                          {!c.notifications_enabled && (
                            <span
                              className="sa-client-off"
                              title="Portal notifications are turned off for this client"
                            >
                              email off
                            </span>
                          )}
                          <span className="ct" title="Signed-in portal accounts">
                            {c.portal_accounts}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Section>

            {/* 6 — email switch + when to send */}
            <Section
              n={6}
              title="When to send"
              hint={d.emailClients ? "timing & reminders" : "banner only"}
            >
              <EmailSwitch
                on={d.emailClients}
                onToggle={() => patch({ emailClients: !d.emailClients })}
              />
              {d.emailClients ? (
                <WhenToSend d={d} patch={patch} funnel={funnel} />
              ) : (
                <div
                  className="sa-sched-info"
                  style={{ ["--accent" as string]: TYPE_META[d.alertType].tone }}
                >
                  <span className="ic">
                    <SAIcon name="eye" />
                  </span>
                  <div className="tx">
                    <div className="a">Banner only. No email will be sent.</div>
                    <div className="b">
                      Clients see this in the portal banner the moment you
                      publish, and it stays until you post a closing status. No
                      announcement email goes out.
                    </div>
                  </div>
                </div>
              )}
            </Section>

            {/* 7 — recipients */}
            <Section
              n={7}
              title="Email recipients"
              hint={d.emailClients ? "resolved before you publish" : "None, banner only"}
            >
              {d.emailClients ? (
                <RecipientPanel d={d} funnel={funnel} />
              ) : (
                <div className="sa-recip-empty">
                  Banner only. No one will be emailed. Turn the email switch
                  above back on to notify the targeted tenants.
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* ===== splitter ===== */}
        <div
          className="sa-resizer"
          id="sa-resizer"
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize the preview (double-click to reset)"
          onDoubleClick={() => {
            localStorage.removeItem("sa-pane-w");
            setPaneW(480);
          }}
        >
          <span className="grip" />
        </div>

        {/* ===== preview ===== */}
        <div className="sa-pane">
          <div className="sa-pane-head">
            <span className="sa-pane-title">
              <SAIcon name="eye" /> Live{" "}
              {effectivePreview === "email" ? "email" : "banner"} preview
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                className={`sa-pane-refresh ${refreshing ? "spin" : ""}`}
                onClick={onRefresh}
                title="Regenerate the customer display from your edits"
              >
                <SAIcon name="refresh" /> Refresh
              </button>
              {d.emailClients && (
                <div className="sa-pane-toggle">
                  <button
                    type="button"
                    className={preview === "email" ? "active" : ""}
                    onClick={() => setPreview("email")}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={preview === "banner" ? "active" : ""}
                    onClick={() => setPreview("banner")}
                  >
                    Banner
                  </button>
                </div>
              )}
            </div>
          </div>

          {effectivePreview === "email" && multiClient && (
            <div className="sa-pane-as">
              <span className="lbl">Previewing as</span>
              <select
                value={previewAs ?? ""}
                onChange={(e) => setPreviewAs(e.target.value || null)}
                disabled={d.visibility === "all"}
              >
                {d.visibility === "all" ? (
                  <option value="">All clients (generic copy)</option>
                ) : (
                  targetedClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
              <span className="cnt">
                {d.visibility === "all"
                  ? `1 of ${clients.length} copies`
                  : `${Math.max(0, d.selected.indexOf(previewAs ?? "")) + 1} of ${
                      targetedClients.length
                    } copies`}
              </span>
            </div>
          )}

          <div className="sa-preview-surface">
            {summaryPreview.updating && <p className="sa-preview-updating">Updating preview</p>}
            {effectivePreview === "email" ? (
              <EmailPreview d={d} clientName={previewClientName} summary={summaryPreview} />
            ) : (
              <BannerPreview d={d} summary={summaryPreview} />
            )}
          </div>
          <div className="sa-preview-note">
            <SAIcon name="activity" />
            <span>
              {d.emailClients
                ? "This mirrors what recipients receive. Posting a new status emails the update to everyone targeted."
                : "Banner only. Clients see this in the portal but receive no email. Posting a new status updates the banner without emailing."}
            </span>
          </div>
        </div>
      </div>

      {toast && (
        <div className="sa-toast show">
          <SAIcon name="check" />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
}

function Section({
  n,
  title,
  hint,
  children,
}: {
  n: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sa-sec">
      <div className="sa-sec-num">
        <span className="n">{n}</span>
        <h3>{title}</h3>
        {hint && <span className="hint">{hint}</span>}
      </div>
      <div className="sa-sec-body">{children}</div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sa-field">
      <label className="sa-fl" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

function EmailSwitch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  // Per-alert "banner only" switch (#39). Mirrors the reminder toggle's markup
  // so it inherits the same card + switch styling.
  return (
    <div className="sa-rem-wrap" style={{ marginBottom: 12 }}>
      <div className="sa-rem">
        <span className="ic">
          <SAIcon name="mail" />
        </span>
        <div className="tx">
          <div className="a">Email clients about this alert</div>
          <div className="b">
            On = the targeted tenants get the announcement email. Off = banner
            only: it still shows in the portal, but no email goes out.
          </div>
        </div>
        <span
          className={`toggle toggle-admin ${on ? "toggle-on" : ""}`}
          role="switch"
          aria-checked={on}
          aria-label="Email clients about this alert"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") &&
            (e.preventDefault(), onToggle())
          }
        >
          <span className="toggle-knob" />
        </span>
      </div>
    </div>
  );
}

function WhenToSend({
  d,
  patch,
  funnel,
}: {
  d: DraftState;
  patch: (p: Partial<DraftState>) => void;
  funnel: RecipientFunnel | null;
}) {
  const recip = funnel?.will_email ?? 0;

  if (d.alertType === "outage") {
    return (
      <>
        <div className="sa-sched-info" style={{ ["--accent" as string]: "#C4262E" }}>
          <span className="ic">
            <SAIcon name="outage" />
          </span>
          <div className="tx">
            <div className="a">Sends the moment you publish</div>
            <div className="b">
              Incidents go out immediately. Each status you post afterwards
              (Investigating → Resolved) re-emails everyone targeted.
            </div>
          </div>
        </div>
        <div className="sa-rem-wrap" style={{ marginTop: 12 }}>
          <div className="sa-rem">
            <span className="ic">
              <SAIcon name="bell" />
            </span>
            <div className="tx">
              <div className="a">Updates re-send as you post them</div>
              <div className="b">
                Each new status emails everyone targeted. There is nothing to
                schedule.
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const isMnt = d.alertType === "maintenance";
  const leads = REMINDER_LEADS[d.alertType];
  const remAt = d.reminderOn ? reminderAtLocal(d.windowStart, d.reminderLead) : null;
  const sendDate = d.sendMode === "schedule" ? d.sendAt || d.windowStart : null;
  const leadLabel = leads.find((l) => l[0] === d.reminderLead)?.[1].toLowerCase() ?? "";

  return (
    <>
      {/* window / go-live */}
      <div className="sa-sched-card" style={{ ["--accent" as string]: TYPE_META[d.alertType].tone }}>
        <div className="sa-sched-head">
          <span className="ic">
            <SAIcon name={isMnt ? "clock" : "enhancement"} />
          </span>
          <span className="t">{isMnt ? "Maintenance window:" : "Go-live:"}</span>
          <span className="badge">{isMnt ? "expected downtime" : "when it ships"}</span>
        </div>
        {isMnt ? (
          <div className="sa-dt">
            <Field label="Starts">
              <input
                type="datetime-local"
                className="sa-in"
                value={d.windowStart}
                onChange={(e) => patch({ windowStart: e.target.value })}
              />
            </Field>
            <Field label="Ends">
              <input
                type="datetime-local"
                className="sa-in"
                value={d.windowEnd}
                onChange={(e) => patch({ windowEnd: e.target.value })}
              />
            </Field>
          </div>
        ) : (
          <Field label="Goes live">
            <input
              type="datetime-local"
              className="sa-in"
              value={d.windowStart}
              onChange={(e) => patch({ windowStart: e.target.value })}
            />
          </Field>
        )}
      </div>

      {/* send mode */}
      <div className="sa-sched-card">
        <div className="sa-sched-head">
          <span className="ic" style={{ background: "var(--iq-violet-soft)", color: "var(--iq-violet)" }}>
            <SAIcon name="mail" />
          </span>
          <span className="t">Send the announcement email</span>
        </div>
        <div className="sa-sendmode">
          <button
            type="button"
            className={`sa-sendopt ${d.sendMode === "now" ? "active" : ""}`}
            onClick={() => patch({ sendMode: "now" })}
          >
            <div className="nm">
              <span className="r" />
              Now
            </div>
            <div className="ds">Goes out the moment you publish.</div>
          </button>
          <button
            type="button"
            className={`sa-sendopt ${d.sendMode === "schedule" ? "active" : ""}`}
            onClick={() => patch({ sendMode: "schedule" })}
          >
            <div className="nm">
              <span className="r" />
              Schedule it
            </div>
            <div className="ds">Queue it for a date &amp; time.</div>
          </button>
        </div>
        {d.sendMode === "schedule" && (
          <div className="sa-field" style={{ marginTop: 12, marginBottom: 0 }}>
            <label className="sa-fl">Send the announcement on</label>
            <input
              type="datetime-local"
              className="sa-in"
              value={d.sendAt || d.windowStart}
              onChange={(e) => patch({ sendAt: e.target.value })}
            />
          </div>
        )}
      </div>

      {/* reminder */}
      <div className="sa-rem-wrap">
        <div className="sa-rem">
          <span className="ic">
            <SAIcon name="bell" />
          </span>
          <div className="tx">
            <div className="a">Auto-reminder before it happens</div>
            <div className="b">
              Re-sends the same email to the same recipients ahead of the{" "}
              {isMnt ? "window" : "go-live"}.
            </div>
          </div>
          <span
            className={`toggle toggle-admin ${d.reminderOn ? "toggle-on" : ""}`}
            role="switch"
            aria-checked={d.reminderOn}
            tabIndex={0}
            onClick={() => patch({ reminderOn: !d.reminderOn })}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              (e.preventDefault(), patch({ reminderOn: !d.reminderOn }))
            }
          >
            <span className="toggle-knob" />
          </span>
        </div>
        {d.reminderOn && (
          <div className="sa-rem-lead">
            <label className="sa-fl" htmlFor="sa-rem-lead">
              Send the reminder
            </label>
            <select
              id="sa-rem-lead"
              className="sa-in"
              value={d.reminderLead}
              onChange={(e) => patch({ reminderLead: e.target.value })}
            >
              {leads.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* live recap */}
      <div className="sa-sched-recap">
        <SAIcon name="bell" />
        <span>
          {sendDate ? (
            <>
              The announcement emails{" "}
              <b>
                {recip.toLocaleString()} recipient{recip === 1 ? "" : "s"}
              </b>{" "}
              on <b>{fmtAwstShort(localToIso(sendDate))}</b>.
            </>
          ) : (
            <>
              The announcement emails{" "}
              <b>
                {recip.toLocaleString()} recipient{recip === 1 ? "" : "s"}
              </b>{" "}
              <b>the moment you publish</b>.
            </>
          )}{" "}
          {d.reminderOn && remAt ? (
            <>
              A reminder then re-sends <b>{fmtAwstShort(remAt.toISOString())}</b>
              {leadLabel ? <>, {leadLabel}.</> : "."}
            </>
          ) : (
            <>No reminder is scheduled.</>
          )}
        </span>
      </div>
    </>
  );
}

/** "today at 5:02am" / "yesterday at 5:01am" / "28 Aug" — AWST, the clock the
 *  admin reading this panel is on. The mirror stores UTC (Brief hard rule);
 *  this is display only. */
function formatSyncedAt(iso: string | null): string {
  if (!iso) return "never";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "unknown";
  const time = when.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Australia/Perth",
  });
  const day = (d: Date) =>
    d.toLocaleDateString("en-AU", { timeZone: "Australia/Perth" });
  const now = new Date();
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (day(when) === day(now)) return `today at ${time}`;
  if (day(when) === day(yesterday)) return `yesterday at ${time}`;
  return when.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    timeZone: "Australia/Perth",
  });
}

function FunnelRow({
  icon,
  cls,
  a,
  b,
  val,
}: {
  icon: SAIconName;
  cls?: string;
  a: string;
  b: string;
  val?: string;
}) {
  return (
    <div className={`sa-recip-row ${cls ?? ""}`}>
      <SAIcon name={icon} />
      <div className="tx">
        <div className="a">{a}</div>
        <div className="b">{b}</div>
      </div>
      {val != null && <div className="val">{val}</div>}
    </div>
  );
}

function RecipientPanel({
  d,
  funnel,
}: {
  d: DraftState;
  funnel: RecipientFunnel | null;
}) {
  const isIncident = d.alertType === "outage";
  const scopeText =
    d.visibility === "all"
      ? `across all ${funnel?.targeted_company_count ?? ""} clients`
      : `across ${d.selected.length} client${d.selected.length === 1 ? "" : "s"}`;

  const noneSelected = d.visibility === "specific" && d.selected.length === 0;

  // The headline is the WHOLE audience: portal accounts plus the licensed Entra
  // mailboxes with no portal account. Those two used to be a real number and a
  // "at send time" placeholder respectively, so the headline could only honestly
  // count the portal half. Now that the Entra half is resolved from the mirror
  // before publish, splitting them here would just under-report how many people
  // an alert actually reaches. The breakdown stays one line below.
  const portalCount = funnel?.will_email ?? 0;
  const entraCount = funnel?.cipp_will_email ?? 0;
  const totalCount = portalCount + entraCount;
  const breakdown =
    entraCount > 0
      ? `${portalCount.toLocaleString()} portal + ${entraCount.toLocaleString()} Entra mailbox${
          entraCount === 1 ? "" : "es"
        } ${scopeText}`
      : scopeText;

  return (
    <div className="sa-recip">
      <div className="sa-recip-top">
        <div className="sa-recip-big">{totalCount.toLocaleString()}</div>
        <div className="tx">
          <div className="l">
            {totalCount === 1 ? "person will be" : "people will be"} emailed on publish
          </div>
          <div className="s">{breakdown}</div>
        </div>
      </div>
      <div className="sa-recip-rows">
        {noneSelected ? (
          <div className="sa-recip-empty">
            Select at least one client above to resolve recipients.
          </div>
        ) : !funnel ? (
          <div className="sa-recip-empty">Resolving…</div>
        ) : (
          <>
            <FunnelRow
              icon="server"
              a="Portal roster"
              b="All provisioned accounts for the targeted tenants"
              val={funnel.roster.toLocaleString()}
            />
            {funnel.never_signed_in > 0 && (
              <FunnelRow
                icon="users"
                cls="force"
                a="Never signed in"
                b="Provisioned but not yet activated, emailed anyway so the alert reaches them"
                val={`${funnel.never_signed_in.toLocaleString()} included`}
              />
            )}
            {funnel.email_off_count > 0 && (
              <FunnelRow
                icon="building"
                cls="warn"
                a={`${funnel.email_off_count} client${funnel.email_off_count === 1 ? "" : "s"} have email turned off`}
                b={`Banner only. Enable per client to email: ${
                  funnel.email_off_company_names.slice(0, 2).join(", ") ||
                  `${funnel.email_off_count} clients`
                }`}
                val={`−${funnel.email_off_accounts.toLocaleString()}`}
              />
            )}
            {isIncident ? (
              <FunnelRow
                icon="bell"
                cls="force"
                a="Force-send. Overrides mute and quiet hours"
                b="Outages reach everyone, even opted-out staff"
                val="keep all"
              />
            ) : (
              funnel.muted > 0 && (
                <FunnelRow
                  icon="bell"
                  cls="warn"
                  a="Muted notifications"
                  b="Muted users still see the banner"
                  val={`−${funnel.muted}`}
                />
              )
            )}
            {funnel.email_channel_off > 0 && (
              <FunnelRow
                icon="mail"
                cls="warn"
                a="Email channel turned off"
                b="These users opted out of email entirely"
                val={`−${funnel.email_channel_off}`}
              />
            )}
            {funnel.suppressed > 0 && (
              <FunnelRow
                icon="mail"
                cls="warn"
                a="Bounced / suppressed"
                b="On the global suppression list"
                val={`−${funnel.suppressed}`}
              />
            )}
            <FunnelRow
              icon="check"
              cls="result"
              a="Will be emailed on publish"
              b={
                funnel.personalised_copies > 1
                  ? `${funnel.personalised_copies} personalised copies, one per client`
                  : "Immediate send"
              }
              val={funnel.will_email.toLocaleString()}
            />
            {/* Non-portal Entra mailbox audience (issue #32). Resolved from the
                mirror the 05:00 AWST sweep fills, so this is a real number
                before publish rather than the old "at send time" placeholder.
                The emails themselves are still sent by the scheduler shortly
                after publish, not on the publish click. */}
            {funnel.cipp_enabled && !funnel.cipp_lookup_ok && (
              /* The mirror read itself failed. Showing "0 / none found" here
                 would be the same confident lie the old 8-second-timeout path
                 told, so say plainly that the number is unknown. */
              <FunnelRow
                icon="globe"
                cls="warn"
                a="Licensed Entra mailboxes (no portal account)"
                b="Could not be resolved just now. They are still emailed after publish"
                val="unknown"
              />
            )}
            {funnel.cipp_enabled && funnel.cipp_lookup_ok && (
              <>
                {funnel.cipp_unconnected_companies > 0 && (
                  <FunnelRow
                    icon="building"
                    cls="warn"
                    a="No Entra tenant connected"
                    b="Set the client's M365 tenant id in Configure to reach staff without a portal account"
                    val={`${funnel.cipp_unconnected_companies.toLocaleString()} client${
                      funnel.cipp_unconnected_companies === 1 ? "" : "s"
                    }`}
                  />
                )}
                {funnel.cipp_never_synced_companies > 0 && (
                  <FunnelRow
                    icon="globe"
                    cls="warn"
                    a="Entra mailboxes not synced yet"
                    b="Onboarded since the last sync. Resolved live when this publishes"
                    val={`${funnel.cipp_never_synced_companies.toLocaleString()} client${
                      funnel.cipp_never_synced_companies === 1 ? "" : "s"
                    }`}
                  />
                )}
                {funnel.cipp_failed_companies > 0 && (
                  <FunnelRow
                    icon="globe"
                    cls="warn"
                    a="Last Entra sync failed"
                    b="Showing the last known mailboxes for these clients"
                    val={`${funnel.cipp_failed_companies.toLocaleString()} client${
                      funnel.cipp_failed_companies === 1 ? "" : "s"
                    }`}
                  />
                )}
                {/* A bare "0" is only meaningful once the rows above have
                    ruled out the two reasons a zero is NOT "nobody to email":
                    no tenant connected, and never synced. */}
                {funnel.cipp_mailboxes === 0 &&
                  funnel.cipp_unconnected_companies === 0 &&
                  funnel.cipp_never_synced_companies === 0 && (
                    <FunnelRow
                      icon="globe"
                      a="Licensed Entra mailboxes (no portal account)"
                      b="None found beyond the portal roster"
                      val="0"
                    />
                  )}
                {funnel.cipp_mailboxes > 0 && (
                  <>
                    <FunnelRow
                      icon="globe"
                      a="Licensed Entra mailboxes (no portal account)"
                      b={
                        funnel.cipp_stale_companies > 0
                          ? `Last synced ${formatSyncedAt(funnel.cipp_as_of)}. May be out of date`
                          : `Licensed, enabled mailboxes not yet on the portal (as of ${formatSyncedAt(
                              funnel.cipp_as_of,
                            )})`
                      }
                      val={funnel.cipp_mailboxes.toLocaleString()}
                    />
                    {funnel.cipp_domain_blocked > 0 && (
                      <FunnelRow
                        icon="building"
                        cls="warn"
                        a="Client mail domain not allow-listed"
                        b="Add the domain to the recipient allow-list to email these"
                        val={`−${funnel.cipp_domain_blocked.toLocaleString()}`}
                      />
                    )}
                    {funnel.cipp_suppressed > 0 && (
                      <FunnelRow
                        icon="mail"
                        cls="warn"
                        a="Bounced / suppressed"
                        b="On the global suppression list"
                        val={`−${funnel.cipp_suppressed.toLocaleString()}`}
                      />
                    )}
                    <FunnelRow
                      icon="globe"
                      cls="result"
                      a="Also emailed via Entra"
                      b="Non-portal mailboxes, sent shortly after publish"
                      val={`+${funnel.cipp_will_email.toLocaleString()}`}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- preview components -------------------------------------------------- //

/**
 * The maintenance window / go-live card in the email preview. Mirrors
 * _sa_window_html in backend/app/integrations/smtp2go/templates.py — keep the
 * two in step, this is what tells the sender the times actually go out.
 */
function WindowPreviewCard({ d }: { d: DraftState }) {
  const isMnt = d.alertType === "maintenance";
  if (!isMnt && d.alertType !== "enhancement") return null;

  const start = windowParts(localToIso(d.windowStart));
  const end = isMnt ? windowParts(localToIso(d.windowEnd)) : null;
  if (!start && !end) return null;

  const rows: [string, string][] = [];
  if (start) {
    rows.push([isMnt ? "Starts" : "Goes live", `${start.date}, ${start.time} AWST`]);
  }
  if (end) rows.push(["Ends", `${end.date}, ${end.time} AWST`]);
  const dur = isMnt
    ? windowDuration(localToIso(d.windowStart), localToIso(d.windowEnd))
    : null;

  return (
    <div className="sa-ep-svc">
      <div className="sa-ep-svc-head">
        <span>{isMnt ? "Maintenance window" : "Go-live"}</span>
        {dur && <span className="count">{dur}</span>}
      </div>
      {rows.map(([label, value]) => (
        <div className="sa-ep-win-row" key={label}>
          <span className="l">{label}</span>
          <span className="v">{value}</span>
        </div>
      ))}
    </div>
  );
}

const PREVIEW_DEBOUNCE_MS = 200;
const PREVIEW_SLOW_MS = 600;
const LENGTH_WARNING = "Keep the summary under 4,000 characters.";

interface LiveSummary {
  rendered: RenderedSummary | null;
  failed: boolean;
  updating: boolean;
}

function summaryHasWindow(type: AlertType, start: string, end: string): boolean {
  if (type === "maintenance") return start.trim() !== "" && end.trim() !== "";
  if (type === "enhancement") return start.trim() !== "";
  return true;
}

function summaryWarnings(summary: LiveSummary, overLimit: boolean): string[] {
  const server = summary.failed ? [] : (summary.rendered?.warnings ?? []);
  if (overLimit && !server.includes(LENGTH_WARNING)) return [...server, LENGTH_WARNING];
  return server;
}

function useLiveSummaryPreview(
  body: string,
  alertType: AlertType,
  hasWindow: boolean,
  bodyFormat: AnnouncementBodyFormat,
): LiveSummary {
  const [rendered, setRendered] = useState<RenderedSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    let slowTimer: number | undefined;
    const debounce = window.setTimeout(() => {
      slowTimer = window.setTimeout(() => {
        if (!ac.signal.aborted) setUpdating(true);
      }, PREVIEW_SLOW_MS);
      api.announcements
        .renderSummary(
          {
            body,
            body_format: bodyFormat,
            alert_type: alertType,
            has_window: hasWindow,
          },
          ac.signal,
        )
        .then((result) => {
          if (ac.signal.aborted) return;
          setRendered(result);
          setFailed(false);
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted) return;
          if (err instanceof DOMException && err.name === "AbortError") return;
          setFailed(true);
        })
        .finally(() => {
          if (slowTimer !== undefined) window.clearTimeout(slowTimer);
          if (!ac.signal.aborted) setUpdating(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      ac.abort();
      window.clearTimeout(debounce);
      if (slowTimer !== undefined) window.clearTimeout(slowTimer);
      setUpdating(false);
    };
  }, [body, alertType, hasWindow, bodyFormat]);

  return { rendered, failed, updating };
}

/** Cmd on Apple platforms, Ctrl elsewhere. userAgentData is the current hint. */
function modifierLabel(): "Cmd" | "Ctrl" {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    "";
  return /mac|iphone|ipad|ipod/i.test(platform) ? "Cmd" : "Ctrl";
}

function SummaryField({
  value,
  onChange,
  warnings,
  overLimit,
  showConversion,
}: {
  value: string;
  onChange: (value: string) => void;
  warnings: string[];
  overLimit: boolean;
  showConversion: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingSel = useRef<{ start: number; end: number } | null>(null);
  const barRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const barIndexRef = useRef(0);
  const [barIndex, setBarIndex] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const mod = useMemo(modifierLabel, []);
  const count = value.length;

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const sel = pendingSel.current;
    if (!ta || !sel) return;
    ta.setSelectionRange(sel.start, sel.end);
    pendingSel.current = null;
  }, [value]);

  function run(edit: SummaryEdit | null) {
    const ta = textareaRef.current;
    if (!ta || !edit) return;
    pendingSel.current = { start: edit.selStart, end: edit.selEnd };
    applySummaryEdit(ta, edit);
    if (ta.value !== value) onChange(ta.value);
  }

  function editWith(fn: (current: string, start: number, end: number) => SummaryEdit) {
    const ta = textareaRef.current;
    if (!ta) return;
    run(fn(ta.value, ta.selectionStart ?? 0, ta.selectionEnd ?? 0));
  }

  function onSummaryKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    const modKey = e.metaKey || e.ctrlKey;
    if (modKey && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "b") {
      e.preventDefault();
      editWith(toggleBold);
      return;
    }
    if (modKey && e.shiftKey && !e.altKey && (e.code === "Digit8" || e.key === "8" || e.key === "*")) {
      e.preventDefault();
      editWith(toggleBulletList);
      return;
    }
    if (modKey && e.shiftKey && !e.altKey && (e.code === "Digit7" || e.key === "7" || e.key === "&")) {
      e.preventDefault();
      editWith(toggleNumberedList);
      return;
    }
    if (modKey && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
      e.preventDefault();
      editWith(insertLink);
      return;
    }
    if (e.key === "Enter" && !modKey && !e.altKey) {
      const ta = e.currentTarget;
      const next = continueList(ta.value, ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
      if (!next) return;
      e.preventDefault();
      run(next);
    }
  }

  function onSummaryPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const ta = e.currentTarget;
    const next = pasteLink(
      ta.value,
      ta.selectionStart ?? 0,
      ta.selectionEnd ?? 0,
      e.clipboardData.getData("text"),
    );
    if (!next) return;
    e.preventDefault();
    run(next);
  }

  function moveBar(next: number) {
    const i = (next + 5) % 5;
    barIndexRef.current = i;
    setBarIndex(i);
    barRefs.current[i]?.focus();
  }

  function onBarKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      moveBar(barIndexRef.current + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveBar(barIndexRef.current - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      moveBar(0);
    } else if (e.key === "End") {
      e.preventDefault();
      moveBar(4);
    }
  }

  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  return (
    <Field label="Summary (the lede shown to clients)" htmlFor="sa-summary">
      <div className="sa-rt">
        <div
          className="sa-rt-bar"
          role="toolbar"
          aria-label="Summary formatting"
          aria-controls="sa-summary"
          onKeyDown={onBarKeyDown}
        >
          <button
            type="button"
            className="sa-rt-btn"
            aria-label="Bold"
            title={`Bold (${mod}+B)`}
            tabIndex={barIndex === 0 ? 0 : -1}
            ref={(el) => {
              barRefs.current[0] = el;
            }}
            onMouseDown={keepFocus}
            onClick={() => editWith(toggleBold)}
          >
            <SAIcon name="bold" />
          </button>
          <button
            type="button"
            className="sa-rt-btn"
            aria-label="Bulleted list"
            title={`Bulleted list (${mod}+Shift+8)`}
            tabIndex={barIndex === 1 ? 0 : -1}
            ref={(el) => {
              barRefs.current[1] = el;
            }}
            onMouseDown={keepFocus}
            onClick={() => editWith(toggleBulletList)}
          >
            <SAIcon name="list" />
          </button>
          <button
            type="button"
            className="sa-rt-btn"
            aria-label="Numbered list"
            title={`Numbered list (${mod}+Shift+7)`}
            tabIndex={barIndex === 2 ? 0 : -1}
            ref={(el) => {
              barRefs.current[2] = el;
            }}
            onMouseDown={keepFocus}
            onClick={() => editWith(toggleNumberedList)}
          >
            <SAIcon name="listOrdered" />
          </button>
          <button
            type="button"
            className="sa-rt-btn"
            aria-label="Link"
            title={`Link (${mod}+K)`}
            tabIndex={barIndex === 3 ? 0 : -1}
            ref={(el) => {
              barRefs.current[3] = el;
            }}
            onMouseDown={keepFocus}
            onClick={() => editWith(insertLink)}
          >
            <SAIcon name="link" />
          </button>
          <button
            type="button"
            className="sa-rt-help"
            aria-expanded={helpOpen}
            aria-controls="sa-summary-help"
            tabIndex={barIndex === 4 ? 0 : -1}
            ref={(el) => {
              barRefs.current[4] = el;
            }}
            onMouseDown={keepFocus}
            onClick={() => setHelpOpen((open) => !open)}
          >
            Formatting help
          </button>
        </div>
        <textarea
          id="sa-summary"
          className="sa-in sa-rt-input"
          aria-describedby={showConversion ? "sa-summary-convert sa-summary-hint" : "sa-summary-hint"}
          aria-invalid={overLimit || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onSummaryKeyDown}
          onPaste={onSummaryPaste}
          ref={textareaRef}
        />
      </div>
      {showConversion && (
        <p id="sa-summary-convert" className="sa-rt-convert">
          {
            "This alert was written before formatting was available. It will switch to formatted text if you edit the summary."
          }
        </p>
      )}
      <div id="sa-summary-hint" className="sa-rt-hint">
        <span>
          <code>**bold**</code>
          {"  "}
          <code>- bullets</code>
          {"  "}
          <code>1. numbers</code>
          {"  "}
          Paste a link. Blank line = new paragraph. Put times in the window fields.
        </span>
        <span className={overLimit ? "cnt over" : "cnt"}>
          {count.toLocaleString("en-AU")} / {SUMMARY_LIMIT.toLocaleString("en-AU")}
        </span>
      </div>
      {helpOpen && (
        <div id="sa-summary-help" className="sa-rt-helpcard">
          Bold: <code>**Planned outage**</code>
          <br />
          Bulleted list: start a line with &quot;- &quot;
          <br />
          Numbered list: start a line with &quot;1. &quot;
          <br />
          Link: paste https://... or use [text](https://...)
          <br />
          New paragraph: leave a blank line. A single Enter keeps a line break.
          <br />
          Put start and end times in the Maintenance window fields. Clients see them in AWST.
          <br />
          Not supported: headings, images, tables and HTML. They show as typed.
        </div>
      )}
      {warnings.length > 0 && (
        <ul className="sa-rt-warn">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </Field>
  );
}

function SummaryPreviewBody({
  d,
  summary,
  variant,
}: {
  d: DraftState;
  summary: LiveSummary;
  variant: "email" | "portal";
}) {
  if (summary.rendered && !summary.failed && summary.rendered.email_html) {
    return (
      <RichSummary className="sa-ep-lede" html={summary.rendered.email_html} variant={variant} />
    );
  }
  return (
    <>
      <div className="sa-ep-lede sa-ep-plain">{d.lede}</div>
      {summary.failed && (
        <p className="sa-preview-fallback">Preview unavailable. Showing plain text.</p>
      )}
    </>
  );
}

function EmailPreview({
  d,
  clientName,
  summary,
}: {
  d: DraftState;
  clientName: string | null;
  summary: LiveSummary;
}) {
  const tone = emailTone(d.alertType, d.state);
  const meta = TYPE_META[d.alertType];
  const greeting = clientName ? `Dear ${clientName} Team,` : "Dear Team,";
  const statusSub =
    d.alertType === "outage"
      ? "Our team is on it"
      : d.alertType === "maintenance"
        ? "Planned work"
        : "Rolling out";

  // For outages, show the running status timeline up to the current state.
  const lifecycle = LIFECYCLE_STATES[d.alertType];
  const currentIdx = lifecycle.findIndex((s) => s[0] === d.state);

  return (
    <div className="sa-ep" data-tone={tone}>
      <span className="sa-ep-eyebrow">
        <SAIcon name={meta.glyph} /> {meta.label}
      </span>
      <h1 className="sa-ep-headline">{d.title || "Untitled status alert"}</h1>
      <p className="sa-ep-greeting">{greeting}</p>
      <SummaryPreviewBody d={d} summary={summary} variant="email" />
      {summary.rendered && !summary.failed && (
        <details className="sa-ep-textpart">
          <summary>Plain-text version</summary>
          <pre>{summary.rendered.text}</pre>
        </details>
      )}

      <div className="sa-ep-card">
        <div className="sa-ep-card-head">
          <span className="sa-ep-pulse">
            <SAIcon name={meta.glyph} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sa-ep-state">{stateLabel(d.state)}</div>
            <div className="sa-ep-statesub">
              {d.affectedSite || statusSub}
            </div>
          </div>
          {d.reference && (
            <div className="sa-ep-ref">
              <div className="l">Reference</div>
              <div className="v">{d.reference}</div>
            </div>
          )}
        </div>
      </div>

      <WindowPreviewCard d={d} />

      {d.services.length > 0 && (
        <div className="sa-ep-svc">
          <div className="sa-ep-svc-head">
            <span>Affected services</span>
            <span className="count">
              {d.services.length} service{d.services.length === 1 ? "" : "s"}
            </span>
          </div>
          {d.services.map((s, i) => (
            <div className="sa-ep-svc-item" key={i}>
              <span className="sa-ep-svc-ico">
                <SAIcon name="server" strokeWidth={1.9} />
              </span>
              <div className="sa-ep-svc-body">
                <div className="sa-ep-svc-name">{s.name}</div>
                <div className="sa-ep-svc-desc">{s.description}</div>
              </div>
              <span className={`sa-ep-pill ${SERVICE_PILL[s.status] ?? "grey"}`}>
                <span className="d" />
                {SERVICE_STATUS_LABEL[s.status] ?? s.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {d.alertType === "outage" && (
        <div className="sa-ep-tl">
          <div className="sa-ep-tl-head">Status updates</div>
          {lifecycle.map(([key, label], i) =>
            i <= currentIdx ? (
              <div
                className={`sa-ep-tl-item ${i === currentIdx ? "current" : ""}`}
                key={key}
              >
                <span className="sa-ep-tl-node" />
                <div className="sa-ep-tl-body">
                  <div className="sa-ep-tl-top">
                    <span className="sa-ep-tl-state">{label}</span>
                    <span className="sa-ep-tl-time">
                      {i === currentIdx ? "now" : "earlier"}
                    </span>
                  </div>
                </div>
              </div>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

function BannerPreview({ d, summary }: { d: DraftState; summary: LiveSummary }) {
  const severity = SEVERITY_OF[d.alertType];
  const isIncident = severity === "incident";
  const rendered = summary.rendered;
  const usePlain = !rendered || summary.failed;
  return (
    <div className="sa-banner-preview">
      <div className="label">In-portal banner (top of every page)</div>
      <div className="announcement-stack">
        <AnnouncementNotice
          mode="preview"
          notice={{
            id: "composer-preview",
            title: d.title || "Untitled status alert",
            body: d.lede,
            severity,
            alertType: d.alertType,
            windowStart: localToIso(d.windowStart),
            windowEnd: localToIso(d.windowEnd),
            bodyHtml: usePlain ? null : rendered.portal_html,
            excerptHtml: usePlain ? "" : rendered.excerpt_html,
            truncated: usePlain ? false : rendered.truncated,
            version: "",
            lifecycleState: d.state,
          }}
        />
      </div>
      {summary.failed && (
        <p className="sa-preview-fallback">Preview unavailable. Showing plain text.</p>
      )}
      <div className="note">
        {isIncident
          ? "Incidents are non-dismissible and pulse until resolved."
          : "Dismissible by users; reappears if updated."}
      </div>
    </div>
  );
}
